/**
 * Ported from apps/admin/lib/format.ts. Display only — nothing here calculates (constitution I).
 * Every monetary value arrives already rounded by the API; every precision arrives from the
 * API's own `decimals` field. A local unit-precision table would be a second copy of a number
 * the API owns.
 */
const MONEY = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

const QUANTITY = new Map<number, Intl.NumberFormat>();

function quantityFormatter(decimals: number): Intl.NumberFormat {
  let formatter = QUANTITY.get(decimals);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    QUANTITY.set(decimals, formatter);
  }
  return formatter;
}

/** ₹12,34,567.89 — Indian digit grouping. `null` means "not priced," never ₹0.00. */
export function formatMoney(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return "not priced";
  return MONEY.format(rate);
}

/** "no_stale_tier_prices" → "No stale tier prices" — titles a machine reason/state code for display. */
export function sentenceCase(code: string): string {
  const words = code.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * "warehouse_supervisor" → "Warehouse Supervisor". Title-cased, not mapped: role codes live only in
 * the API's matrix (guards check 2), so spec §5's own names need the API to send them.
 */
export function roleName(role: string): string {
  return role.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/** Renders at the precision the API declared for that unit. */
export function formatQuantity(quantity: number | null | undefined, decimals: number): string {
  if (quantity === null || quantity === undefined) return "not recorded";
  return quantityFormatter(decimals).format(quantity);
}

/**
 * Every INSTANT is shown in the business's own time zone, whatever zone the browser or server is
 * in. Set per deployment — the country the project runs in — as `VITE_BUSINESS_TIME_ZONE` (an IANA
 * id, e.g. `Asia/Dubai`), India by default. It must match the backend's `Billing:BusinessTimeZone`.
 */
export const BUSINESS_TIME_ZONE: string = import.meta.env.VITE_BUSINESS_TIME_ZONE || "Asia/Kolkata";

/**
 * A plain ISO date ("2026-08-01", no clock) is not an instant: rendered in UTC so a tier's
 * effective date never moves with the browser's zone. A full timestamp is an instant, and its date
 * is the business zone's — so a stacked date/time cell never shows yesterday's date beside today's time.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const timeZone = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? "UTC" : BUSINESS_TIME_ZONE;
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone }).format(new Date(iso));
}

/** Just the clock part of `formatMoment`, for a stacked date/time cell (isomorphic's `DateCell`). */
export function formatTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Intl.DateTimeFormat("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: BUSINESS_TIME_ZONE, timeZoneName: "short" }).format(new Date(iso));
}

/** Absolute instant in the business zone — never relative/elapsed ("3 hours ago"), so a clock skew can't mislead. */
export function formatMoment(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: BUSINESS_TIME_ZONE,
    timeZoneName: "short",
  }).format(new Date(iso));
}

/**
 * "2026-08-01" ↔ a Date at LOCAL midnight, for date pickers. `new Date("2026-08-01")` is UTC
 * midnight and `toISOString()` is UTC, so either one alone shifts the day by the viewer's offset
 * (IST saves the day before; a negative-offset zone shows the day before).
 */
export function parseIsoDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function toIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
